import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation as useI18NTranslation } from "react-i18next";
import { I18NNamespace } from "../enums/I18N";
import useMedia from "../hooks/useMedia";
import { Breakpoint } from "../enums/Breakpoint";
import { useMissingLabelsContext } from "../contexts/MissingLabelsContext";

export type TFunction = (labelKey: string, options?: object) => string;
const MISSING_TRANSLATION_PLACEHOLDER = "...";

export const useTranslation = (mobileBreakpoint?: Breakpoint) => {
  const { i18n, t } = useI18NTranslation();
  const { addMissingLabels, isLabelAlreadyAdded } = useMissingLabelsContext();
  const [missingLabels, setMissingLabels] = useState<string[]>([]);

  const properBreakpoint = useMemo(() => {
    return !!mobileBreakpoint ? mobileBreakpoint : Breakpoint.MaxMobile;
  }, [mobileBreakpoint]);

  const isMobile = useMedia(properBreakpoint);
  const enhancedT: TFunction = useCallback(
    (labelKey: string, options?: object) => {
      const loweredLabelKey = labelKey && labelKey.toLowerCase();
      if (!loweredLabelKey || loweredLabelKey.match(/[^0-9a-zA-Z_]/i)) {
        return labelKey;
      }
      const ns = isMobile ? I18NNamespace.Mobile : I18NNamespace.General;
      const translation = t(loweredLabelKey, {
        ...options,
        defaultValue: null,
        interpolation: { escapeValue: false },
        ns,
      });
      if (
        translation === null &&
        !missingLabels.includes(loweredLabelKey) &&
        !isLabelAlreadyAdded(ns, loweredLabelKey)
      ) {
        setMissingLabels([...missingLabels, loweredLabelKey]);
      }
      return translation === null ? MISSING_TRANSLATION_PLACEHOLDER : translation;
    },
    [t, isMobile, missingLabels, setMissingLabels, isLabelAlreadyAdded]
  );

  useEffect(() => {
    if (missingLabels.length > 0) {
      addMissingLabels(isMobile ? I18NNamespace.Mobile : I18NNamespace.General, missingLabels);
      setMissingLabels([]);
    }
  }, [addMissingLabels, isMobile, missingLabels]);

  return {
    i18n,
    t: enhancedT,
  };
};
